import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../shadcn/components/ui/Dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../shadcn/components/ui/Table';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../shadcn/components/ui/Card';
import { Button } from '../../../../shadcn/components/ui/Button';
import { Input } from '../../../../shadcn/components/ui/Input';
import { Separator } from '../../../../shadcn/components/ui/Separator';
import { Loader2, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { warehouseApi as api } from '../../../../api/warehouse';

const DimensionsCell = ({ childLocation, onUpdated }) => {
    const [isEditing, setIsEditing] = React.useState(false);
    const [draftLength, setDraftLength] = React.useState(childLocation.length ?? 0);
    const [draftWidth, setDraftWidth] = React.useState(childLocation.width ?? 0);
    const [draftHeight, setDraftHeight] = React.useState(childLocation.height ?? 0);
    const [saving, setSaving] = React.useState(false);

    const renderDisplay = () => {
        const lengthInches = childLocation.length ?? 0;
        const widthInches = childLocation.width ?? 0;
        const heightInches = childLocation.height ?? 0;
        const volume = Number(lengthInches) * Number(widthInches) * Number(heightInches);
        if (volume === 0) {
            return 'n/a';
        }
        const volumeFormatted = Number(volume).toLocaleString(undefined, {
            maximumFractionDigits: 2,
        });
        return `${lengthInches} x ${widthInches} x ${heightInches} = ${volumeFormatted} in³`;
    };

    const handleCancel = () => {
        if (saving) return;
        setIsEditing(false);
        setDraftLength(childLocation.length ?? 0);
        setDraftWidth(childLocation.width ?? 0);
        setDraftHeight(childLocation.height ?? 0);
    };

    const handleSave = async () => {
        if (saving) return;
        setSaving(true);
        try {
            const sendParams = {
                ids: [childLocation.id],
                dimensions: {
                    length: Number(draftLength) || 0,
                    width: Number(draftWidth) || 0,
                    height: Number(draftHeight) || 0,
                },
            };
            const response = await api.updateLocations(sendParams);
            if (response.status === false) {
                toast.error(`Error updating dimensions: ${response.msg}`);
                return;
            }
            toast.success('Dimensions updated successfully!');
            setIsEditing(false);
            if (onUpdated) onUpdated();
        } catch (e) {
            toast.error('Error updating dimensions');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex items-center gap-2">
            {isEditing ? (
                <div className="flex flex-col items-start gap-2">
                    <div className="flex items-center gap-1">
                        <Input
                            type="number"
                            step=".01"
                            min="0"
                            value={draftLength}
                            onChange={(e) => setDraftLength(e.target.value)}
                            className="w-20"
                            disabled={saving}
                        />
                        <span className="text-gray-500 text-xs">L</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <Input
                            type="number"
                            step=".01"
                            min="0"
                            value={draftWidth}
                            onChange={(e) => setDraftWidth(e.target.value)}
                            className="w-20"
                            disabled={saving}
                        />
                        <span className="text-gray-500 text-xs">W</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <Input
                            type="number"
                            step=".01"
                            min="0"
                            value={draftHeight}
                            onChange={(e) => setDraftHeight(e.target.value)}
                            className="w-20"
                            disabled={saving}
                        />
                        <span className="text-gray-500 text-xs">H</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {saving && (
                            <Loader2
                                className="h-4 w-4 animate-spin text-gray-500"
                                title="Saving"
                                aria-label="Saving"
                            />
                        )}
                        <Button
                            variant="outline"
                            size="sm"
                            className="border-green-300 text-green-700 hover:bg-green-100 hover:border-green-400"
                            onClick={handleSave}
                            disabled={saving}
                            title="Save"
                        >
                            <Check className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-gray-400"
                            onClick={handleCancel}
                            disabled={saving}
                            title="Cancel"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            ) : (
                <>
                    <span className="flex items-center gap-2">
                        {renderDisplay()}
                        {saving && (
                            <Loader2
                                className="h-4 w-4 animate-spin text-gray-500"
                                title="Saving"
                                aria-label="Saving"
                            />
                        )}
                    </span>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="text-blue-700 hover:bg-blue-50"
                        onClick={() => setIsEditing(true)}
                        disabled={saving}
                        title="Edit dimensions"
                    >
                        <Pencil className="h-4 w-4" />
                    </Button>
                </>
            )}
        </div>
    );
};

const LocationDimensionsModal = ({ location, isOpen, onClose, onUpdated }) => {
    if (!location) return null;

    const childLocations = location?.childLocations || [];
    const showTypeColumn = location?.type === 'REPLENISHMENT';

    const [refreshing, setRefreshing] = React.useState(false);

    const handleUpdated = async () => {
        try {
            setRefreshing(true);
            if (onUpdated) {
                await onUpdated();
            }
        } finally {
            setRefreshing(false);
        }
    };

    const rows = [
        { type: 'Parent', name: location.name, node: location },
        ...childLocations.map((cl) => ({ type: 'Child', name: cl.name, node: cl })),
    ];

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent
                className="max-w-5xl max-h-[80vh] overflow-y-auto"
                style={{ width: '50vw', maxWidth: '1200px' }}
            >
                <DialogHeader className="border-b border-gray-200 pb-4 mb-6">
                    <DialogTitle className="text-2xl font-bold text-gray-800 flex items-center gap-3">
                        Location Dimensions for {location.name}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-6">
                    <Card className="border-blue-200 pb-2 pt-0">
                        <CardHeader className="bg-blue-100 rounded-t-xl pt-3">
                            <CardTitle className="text-lg text-blue-900 flex items-center gap-2">
                                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                Dimensions ({rows.length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="bg-white">
                            <div className="relative">
                                {refreshing && (
                                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                                        <Loader2
                                            className="h-8 w-8 animate-spin text-gray-600"
                                            aria-label="Refreshing"
                                        />
                                    </div>
                                )}
                                <Table>
                                    <TableHeader>
                                        <TableRow className="border-blue-200">
                                            {showTypeColumn && (
                                                <TableHead className="text-blue-900 font-semibold">Type</TableHead>
                                            )}
                                            <TableHead className="text-blue-900 font-semibold">Name</TableHead>
                                            <TableHead className="text-blue-900 font-semibold">
                                                Dimensions (LxWxH=in³)
                                            </TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {rows.map((r) => (
                                            <TableRow key={`${r.type}-${r.node.id}`} className={`hover:bg-blue-50`}>
                                                {showTypeColumn && (
                                                    <TableCell className="font-medium text-gray-800">
                                                        {r.type}
                                                    </TableCell>
                                                )}
                                                <TableCell className="font-medium text-gray-800">{r.name}</TableCell>
                                                <TableCell className="font-medium text-gray-800">
                                                    <DimensionsCell childLocation={r.node} onUpdated={handleUpdated} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>

                    <Separator className="bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default LocationDimensionsModal;
